import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

// MUI
import { TextField, Checkbox } from "@mui/material";

// Components
import RegularButton from "../../components/Buttons/RegularButton";
import MuiAlert from "../../pages/MuiAlert/MuiAlert";
import Loading from "../../pages/commons/Loading";

// Assets
import GoogleIcon from "../../assets/Google.png";
import FacebookIcon from "../../assets/FB.png";

// Firebase
import { auth, facebook, google } from "../../config/firebase";
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

// Redux Slices & APIs
import { setUser } from "../../redux/slices/authSlice";
import { getMessage } from "../../redux/services/message";
import { tagManager } from "../../redux/services/utility";
import {
  useGoogleAuthDetailsMutation,
  useSsoLoginUserMutation,
} from "../../redux/services/authApi";
import { useFindUsernameMutation } from "../../redux/services/userApi";


const SignUpSchema = Yup.object().shape({
  name: Yup.string().max(40).required(" Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password is too short"),
  confirmpassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

const SignUp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const [signUp, signUpResponse] = useSsoLoginUserMutation();
  const [findUsername, findUsernameResponse] = useFindUsernameMutation();
  const [googleAuthDetails, googleAuthResponse] =useGoogleAuthDetailsMutation();



  const [signUpData, setSignUpData] = useState<null | any>(null);
  const [authenticationData, setAuthenticationData] = useState<null | any>( null);
  const [googleData, setGoogleData] = useState<null | any>(null);
  const [statusCode, setStatusCode] = useState({ type: "", code: "" }) as any;
  const [terms, setTerms] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);


  const loginWithSocialMedia = (
    email: string,
    name: string,
    gaccessToken: string,
    gid: string,
    gpicture: string
  ) => {
    signUp({
      email,
      name,
      gaccessToken,
      gid,
      gimageUrl: gpicture,
    });
    tagManager("LOGIN_GOOGLE", email);
  };

  useEffect(() => {
    if (googleAuthResponse.isSuccess) {
      loginWithSocialMedia(
        googleData?.email,
        googleAuthResponse.data.name,
        authenticationData?.accessToken,
        googleAuthResponse.data.sub,
        googleAuthResponse.data.picture
      );
    } else if (googleAuthResponse.isError) {
    }
  }, [googleAuthResponse.isSuccess, googleAuthResponse.isError]);

  const SignInWithGoogle = () => {
    tagManager("LOGIN_GOOGLE");
    signInWithPopup(auth, google)
      .then((result) => {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential?.accessToken;
        setAuthenticationData(credential);
        const user = result.user;
        setGoogleData(user);
        if (result.user.emailVerified === true) {
          googleAuthDetails({ token });
        }
      })
      .catch((error) => {
        setStatusCode({ type: "error", code: getMessage.GOOGLE_LOGIN_FAILED });
        setIsAlertVisible(true);
        setTimeout(() => setIsAlertVisible(false), 5000);
      });
  };


  const SignInWithFaceBook = () => {
    tagManager("LOGIN_FACEBOOK");
    signInWithPopup(auth, facebook)
      .then((result) => {
        const user = result.user;
        const credential = FacebookAuthProvider.credentialFromResult(result);
        const accessToken = credential?.accessToken;
        const profile = user.providerData[0];

        if (accessToken) {
          signUp({
            email: profile?.email,
            name: user.displayName,
            fbAccessToken: accessToken,
            fbId: profile?.uid,
            fbImageUrl: user.photoURL,
          });
        }
      })
      .catch((error) => {
        setStatusCode({ type: "error", code: getMessage.FACEBOOK_LOGIN_FAILED });
        setIsAlertVisible(true);
        setTimeout(() => setIsAlertVisible(false), 5000);
      });
  };




  const handleLogin = async (values: any) => {
    let tracker = {
      email: values?.email,
      name: values?.name,
    }
    tagManager("SIGNUP", tracker)
    findUsername({
      username: values.email,
    });
    setSignUpData(values);
  };


  useEffect(() => {
    if (findUsernameResponse.isSuccess) {
      if (findUsernameResponse.data == true) {
        signUp({
          email: signUpData?.email,
          name: signUpData?.name,
          password: signUpData?.password,
        });
      }
      if (findUsernameResponse.data == false) {
        setStatusCode({ type: "error", code: "USER_ALREADY_EXIST" });
        setIsAlertVisible(true);
        setTimeout(() => {
          setIsAlertVisible(false);
        }, 5000);
      }
    }
  }, [findUsernameResponse.isSuccess]);


  useEffect(() => {
    if (signUpResponse.isSuccess) {
      tagManager("SIGNUP_SUCCESS");
      dispatch(setUser(signUpResponse.data));

      setStatusCode({
        type: "success",
        code: "SIGNUP_SUCCESSFUL",
      });
      setIsAlertVisible(true);
      setTimeout(() => {
        setIsAlertVisible(false);
      }, 5000);

      navigate("/profile/auth/finish");
    }

    if (signUpResponse.isError) {
      let errorData: any = signUpResponse.error;
      let errorCode: any = errorData.data.serviceErrors[0]?.errorCode;

      setStatusCode({
        type: "error",
        code:
          errorCode === "VALIDATION_EXCEPTION"
            ? "SOCIALMEDIA_ERROR"
            : "SIGNUP_FAILED",
      });
      setIsAlertVisible(true);
      setTimeout(() => {
        setIsAlertVisible(false);
      }, 5000);
    }
  }, [signUpResponse.isSuccess, signUpResponse.isError]);

  const handleChangeT = () => {
    setTerms(!terms);
  };

  return (
    <div className="w-full bg-[#F9F4EF]">
      <div className="relative w-full flex lg:hidden justify-center items-center pt-10">
        <img
          src={require("../../assets/login.png")}
          className="w-full sm:w-[500px] md:w-[600px]  md:max-h-[500px] object-cover"
          alt="Auth Illustration"
        />

        {/* Overlayed Text (Responsive for Mobile & Tab) */}
        <div className="absolute top-12 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center w-full px-4">
          <h2 className="text-xl sm:text-2xl md:text-4xl text-black">WEEKEND GETAWAY</h2>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-black mt-2">
            Grab Up to 40% OFF*
          </h1>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row h-screen">
        {/* Form Section */}
        <div className="w-full lg:w-[40%] flex flex-col justify-center items-center p-4">
          <div className="max-w-[400px] w-full">
            {isAlertVisible && (
              <MuiAlert severity={statusCode?.type} messageType={statusCode?.code} />
            )}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-700">
                ca<span className="font-extrabold">RENT</span>al
              </h1>
              <h2 className="text-2xl font-semibold text-black mt-2">Sign up</h2>
            </div>
            <Formik
              validationSchema={SignUpSchema}
              initialValues={{
                name: "",
                password: "",
                confirmpassword: "",
                email: "",
              }}
              onSubmit={handleLogin}
            >
              {({
                handleBlur,
                handleChange,
                handleSubmit,
                values,
                touched,
                errors,
              }) => (
                <div>
                  <div className="mt-3">
                    <TextField
                      label="Full Name"
                      variant="outlined"
                      size="small"
                      type="text"
                      id={"name"}
                      name={"name"}
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your name"
                      className="w-full "
                      error={Boolean(
                        touched.name && errors.name
                      )}
                      helperText={
                        touched.name && errors.name
                      }
                      sx={{
                        backgroundColor: "#E2E2E2",
                        borderRadius: "30px",
                        mb: 2,
                        height: "45px", // Slightly reduced height for a compact look
                        "& .MuiInputBase-root": {
                          display: "flex",
                          alignItems: "center", // Centers input text vertically
                          height: "100%",
                          padding: "0 12px", // Reducing padding inside input container
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "30px",
                          height: "100%",
                          "& input": {
                            padding: "6px 8px", // Reduced padding inside input field
                          },
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "transparent" },
                          "&.Mui-focused fieldset": { borderColor: "transparent" },
                        },
                        "& .MuiInputLabel-root": {
                          top: "50%",
                          left: "12px", // Moves label closer to start
                          transform: "translateY(-50%)", // Centers label vertically
                        },
                        "& .MuiInputLabel-shrink": {
                          top: 6, // Moves label up when focused
                          transform: "translateY(-100%) scale(0.75)",
                        },
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          bottom: "-18px", // Adjusted for better spacing
                          fontSize: "12px",
                          color: "red",
                        },
                      }}
                    />
                  </div>
                  <div className="mt-3">
                    <TextField
                      label="Email"
                      size="small"
                      variant="outlined"
                      type="text"
                      id={"email"}
                      name={"email"}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your email"
                      className="w-full "
                      error={Boolean(
                        touched.email && errors.email
                      )}
                      helperText={
                        touched.email && errors.email
                      }
                      sx={{
                        backgroundColor: "#E2E2E2",
                        borderRadius: "30px",
                        mb: 2,
                        height: "45px", // Slightly reduced height for a compact look
                        "& .MuiInputBase-root": {
                          display: "flex",
                          alignItems: "center", // Centers input text vertically
                          height: "100%",
                          padding: "0 12px", // Reducing padding inside input container
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "30px",
                          height: "100%",
                          "& input": {
                            padding: "6px 8px", // Reduced padding inside input field
                          },
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "transparent" },
                          "&.Mui-focused fieldset": { borderColor: "transparent" },
                        },
                        "& .MuiInputLabel-root": {
                          top: "50%",
                          left: "12px", // Moves label closer to start
                          transform: "translateY(-50%)", // Centers label vertically
                        },
                        "& .MuiInputLabel-shrink": {
                          top: 6, // Moves label up when focused
                          transform: "translateY(-100%) scale(0.75)",
                        },
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          bottom: "-18px", // Adjusted for better spacing
                          fontSize: "12px",
                          color: "red",
                        },
                      }}
                    />

                  </div>
                  <div className="mt-3">
                    <TextField
                      label="Password"
                      variant="outlined"
                      size="small"
                      type="password"
                      id={"password"}
                      name={"password"}
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your password"
                      className="w-full "
                      error={Boolean(
                        touched.password && errors.password
                      )}
                      helperText={
                        touched.password && errors.password
                      }
                      sx={{
                        backgroundColor: "#E2E2E2",
                        borderRadius: "30px",
                        mb: 2,
                        height: "45px", // Slightly reduced height for a compact look
                        "& .MuiInputBase-root": {
                          display: "flex",
                          alignItems: "center", // Centers input text vertically
                          height: "100%",
                          padding: "0 12px", // Reducing padding inside input container
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "30px",
                          height: "100%",
                          "& input": {
                            padding: "6px 8px", // Reduced padding inside input field
                          },
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "transparent" },
                          "&.Mui-focused fieldset": { borderColor: "transparent" },
                        },
                        "& .MuiInputLabel-root": {
                          top: "50%",
                          left: "12px", // Moves label closer to start
                          transform: "translateY(-50%)", // Centers label vertically
                        },
                        "& .MuiInputLabel-shrink": {
                          top: 6, // Moves label up when focused
                          transform: "translateY(-100%) scale(0.75)",
                        },
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          bottom: "-18px", // Adjusted for better spacing
                          fontSize: "12px",
                          color: "red",
                        },
                      }}
                    />
                  </div>
                  <div className="mt-3">
                    <TextField
                      label={"Confirm Password"}
                      variant="outlined"
                      size="small"
                      type="password"
                      name={"confirmpassword"}
                      value={values.confirmpassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your name"
                      className="w-full "
                      error={Boolean(
                        touched.confirmpassword && errors.confirmpassword
                      )}
                      helperText={
                        touched.confirmpassword && errors.confirmpassword
                      }
                      sx={{
                        backgroundColor: "#E2E2E2",
                        borderRadius: "30px",
                        mb: 2,
                        height: "45px", // Slightly reduced height for a compact look
                        "& .MuiInputBase-root": {
                          display: "flex",
                          alignItems: "center", // Centers input text vertically
                          height: "100%",
                          padding: "0 12px", // Reducing padding inside input container
                        },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "30px",
                          height: "100%",
                          "& input": {
                            padding: "6px 8px", // Reduced padding inside input field
                          },
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "transparent" },
                          "&.Mui-focused fieldset": { borderColor: "transparent" },
                        },
                        "& .MuiInputLabel-root": {
                          top: "50%",
                          left: "12px", // Moves label closer to start
                          transform: "translateY(-50%)", // Centers label vertically
                        },
                        "& .MuiInputLabel-shrink": {
                          top: 6, // Moves label up when focused
                          transform: "translateY(-100%) scale(0.75)",
                        },
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          bottom: "-18px", // Adjusted for better spacing
                          fontSize: "12px",
                          color: "red",
                        },
                      }}
                    />
                  </div>
                  <div className="flex mt-3 mb-2  items-center">
                    <Checkbox
                      size="small"
                      color="error"
                      sx={{ padding: 0, paddingRight: 1 }}
                      onChange={handleChangeT}
                    />
                    <p>
                      {" "}
                      I agree to their{" "}
                      <a
                        onClick={() => {
                          navigate("/terms-and-condition");
                        }}
                        className="cursor-pointer text-blue-500"
                      >
                        Terms & Conditions{" "}
                      </a>{" "}
                      <span>& </span>
                      <a
                        onClick={() => {
                          navigate("/privacy-policy");
                        }}
                        className="cursor-pointer text-blue-500"
                      >
                        Privacy Policy{" "}
                      </a>{" "}
                    </p>
                  </div>
                  <div className="lg:flex sm:flex-row items-center gap-5 ">
                    <RegularButton
                      disabled={terms == false}
                      onClick={() => {
                        handleSubmit();
                      }}
                      className="w-full"
                      title={"Sign Up"}
                    />
                  </div>
                  <div>
                    <div className="flex items-center w-full">
                      <div className="border-t-2 w-[33%]"></div>
                      <p className="mb-0 text-center text-[11px] md:text-[16px] w-[33%] text-gray-500 py-4">
                        Or Sign up with
                      </p>
                      <div className="border-b-2 w-[33%]"></div>
                    </div>
                    <div className="flex justify-center mt-3 gap-4 ">
                      <button className="h-9 w-9 flex items-center justify-center" onClick={SignInWithGoogle}>
                        <img src={GoogleIcon} alt="Google" />
                      </button>
                      <button className="h-9 w-9 flex items-center justify-center" onClick={SignInWithFaceBook}>
                        <img src={FacebookIcon} alt="Facebook" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-center text-gray-500 mt-6 cursor-default ">
                      Already have an account?{" "}
                      <span
                        className="text-blue-500 cursor-pointer"
                        onClick={() => {
                          navigate("/profile/auth/login");
                        }}
                      >
                        Sign in
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </Formik>
          </div>
        </div>
        {/* Image Section */}
        <div className="relative w-full lg:w-[60%] hidden lg:flex justify-center items-center p-4">
          <img
            src={require("../../assets/login.png")}
            className="w-[600px] max-h-[300px] md:max-h-[500px] object-cover"
            alt="Auth Illustration"
          />

          {/* Overlayed Text (Centered Properly) */}
          <div className="absolute top-28 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center w-full px-4">
            <h2 className="text-4xl text-black whitespace-nowrap">WEEKEND GETAWAY</h2>
            <h1 className="text-5xl font-bold text-black mt-2 whitespace-nowrap">
              Grab Up to 40% OFF*
            </h1>
          </div>
        </div>
      </div>
      {signUpResponse.isLoading ||
        findUsernameResponse.isLoading ||
        googleAuthResponse.isLoading ? (
        <Loading />
      ) : null}
    </div>
  );
};

export default SignUp;
